/**
 * /scope — the login-gated scope page (HUB-PROJECT-STANDARD §4).
 * Fetches THIS project's own canonical scope JSON from /api/scope/site-scope/
 * with the stored JWT and renders the groups and their checks. Anyone who is
 * not a signed-in SUPERUSER sees only a sign-in prompt — never the scope:
 * the gate lives on the server (403), this page just mirrors it politely.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../App.jsx';
import AIAction from '../components/AIAction.jsx';

/** House-standard scope notes (§3): typed here with a kind; the backend
 * appends to NOTES.md (so it lives IN the scope and survives a re-pull)
 * and forwards to the hub. Voice notes travel via the ? reporter. */
function NoteBox() {
  const [kind, setKind] = useState('note');
  const [body, setBody] = useState('');
  const [msg, setMsg] = useState('');
  const [recOn, setRecOn] = useState(false);
  const [rec, setRec] = useState(null);
  // 🎙 voice → server transcription lands in the box for review.
  const voice = async () => {
    if (recOn) { rec?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecOn(false);
        const fd = new FormData();
        fd.append('audio', new Blob(chunks, { type: 'audio/webm' }), 'note.webm');
        try {
          const d = await api('/scope/comments/assist/', { method: 'POST', form: fd });
          if (d.text) setBody(d.text);
        } catch { /* typing always works */ }
      };
      setRec(r); r.start(); setRecOn(true);
    } catch { /* mic refused */ }
  };
  // ✨ AI drafts the note from the kind + what's typed so far — via the shared
  // AIAction, so it lands in the editable box with a read-back and Undo.
  const bodyBefore = useRef('');
  const draftNote = async () => {
    const d = await api('/scope/comments/assist/',
      { method: 'POST',
        body: { title: `a Vault scope ${kind} note`,
                context: body || 'the current state of the project' } });
    return d.text || null;
  };
  const send = async () => {
    if (!body.trim()) return;
    try {
      const r = await api('/scope/notes/',
                          { method: 'POST', body: { kind, body: body.trim() } });
      setBody('');
      setMsg(r.hub ? '✓ Noted — in the scope and on the hub.'
                   : '✓ Noted in the scope (hub unreachable — it syncs on publish).');
    } catch (e) {
      setMsg(e.data?.detail || e.message);
    }
  };
  return (
    <div className="card mt-6">
      <h2 className="font-semibold text-sm mb-2">Add a scope note</h2>
      <div className="flex gap-2">
        <select className="input !w-auto text-sm" value={kind}
                onChange={(e) => setKind(e.target.value)}>
          {['build', 'produce', 'create', 'followup', 'note'].map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        <input className="input flex-1 text-sm" placeholder="The note…"
               value={body} onChange={(e) => setBody(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && send()} />
        <button className={`text-sm border rounded-xl px-3 ${
                  recOn ? 'border-red-400 text-red-400 animate-pulse'
                        : 'border-edge text-slate-400'}`}
                title={recOn ? 'Stop and transcribe' : 'Speak the note'}
                onClick={voice}>
          🎙
        </button>
        <AIAction size="sm" label="" title="✨ AI drafts the note"
                  run={() => { bodyBefore.current = body; return draftNote(); }}
                  onResult={setBody}
                  onUndo={() => setBody(bodyBefore.current)}
                  readback="Drafted — edit before you add it." />
        <button className="btn-primary !px-4 text-sm" onClick={send}>Add</button>
      </div>
      {msg && <p className="text-xs text-vpurple mt-2">{msg}</p>}
      <p className="text-[10px] text-slate-600 mt-1">
        Appends to NOTES.md (mirrors into the scope, survives re-pulls) and
        forwards to the hub. Voice notes: the ? reporter transcribes them.
      </p>
    </div>
  );
}

/** Rollout §2: ONE static scope item — the function, its steps, its state
 * (✓ done / todo / ⚠ resurfaced), and an ✎ editor adding comments to it by
 * typing, 🎙 voice or ✨ AI. */
function ScopeItem({ t, mark }) {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState('');
  const [recOn, setRecOn] = useState(false);
  const [rec, setRec] = useState(null);
  useEffect(() => {
    if (open) {
      api(`/scope/comments/?test_key=${encodeURIComponent(t.key)}`)
        .then(setThread).catch(() => {});
    }
  }, [open, t.key]);
  const addComment = async () => {
    if (!body.trim()) return;
    await api('/scope/comments/',
              { method: 'POST',
                body: { test_key: t.key, label: t.title, body: body.trim() } });
    setBody('');
    api(`/scope/comments/?test_key=${encodeURIComponent(t.key)}`)
      .then(setThread).catch(() => {});
  };
  const voice = async () => {
    if (recOn) { rec?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach((x) => x.stop());
        setRecOn(false);
        const fd = new FormData();
        fd.append('audio', new Blob(chunks, { type: 'audio/webm' }), 'c.webm');
        try {
          const d = await api('/scope/comments/assist/', { method: 'POST', form: fd });
          if (d.text) setBody(d.text);
        } catch { /* typing works */ }
      };
      setRec(r); r.start(); setRecOn(true);
    } catch { /* mic refused */ }
  };
  const bodyBefore = useRef('');
  const draftComment = async () => {
    const d = await api('/scope/comments/assist/',
      { method: 'POST', body: { title: t.title, context: t.pass_if } });
    return d.text || null;
  };
  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-2">
        <span className={`mt-0.5 text-sm ${
                mark === 'done' ? 'text-emerald-400'
                : mark === 'issue' ? 'text-amber-400' : 'text-slate-600'}`}
              title={mark === 'done' ? 'Complete — pass_if met'
                : mark === 'issue' ? 'Resurfaced — open support issue'
                : 'Still to do'}>
          {mark === 'done' ? '✓' : mark === 'issue' ? '⚠' : '○'}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {t.title}
            {t.dev_only && (
              <span className="ml-2 text-[10px] uppercase text-slate-600
                               border border-edge rounded px-1">dev</span>
            )}
          </p>
          {(t.steps || []).length > 0 && (
            <ol className="text-xs text-slate-500 mt-1 list-decimal ml-4 grid gap-0.5">
              {t.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          )}
          {t.pass_if && (
            <p className="text-xs text-emerald-400/80 mt-1">✓ Pass if: {t.pass_if}</p>
          )}
        </div>
        <button className="text-xs text-slate-500 hover:text-vblue shrink-0"
                title="Comment on this item — type, 🎙 voice, or ✨ AI"
                onClick={() => setOpen(!open)}>
          ✎
        </button>
      </div>
      {open && (
        <div className="mt-2 ml-6 border-l border-edge pl-3">
          {thread.map((c, k) => (
            <p key={k} className="text-xs text-slate-400 py-0.5">
              <span className="text-slate-300">{c.author}:</span> {c.body}
            </p>
          ))}
          <div className="flex gap-1.5 mt-1">
            <input className="input !py-1 text-xs flex-1" placeholder="Comment…"
                   value={body} onChange={(e) => setBody(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && addComment()} />
            <button className={`text-xs border rounded-lg px-2 ${
                      recOn ? 'border-red-400 text-red-400 animate-pulse'
                            : 'border-edge text-slate-400'}`}
                    onClick={voice}>🎙</button>
            <AIAction size="sm" label="" title="✨ AI drafts a comment"
                      run={() => { bodyBefore.current = body; return draftComment(); }}
                      onResult={setBody}
                      onUndo={() => setBody(bodyBefore.current)}
                      readback="Drafted — edit before you add it." />
            <button className="text-xs border border-edge rounded-lg px-2 text-slate-400"
                    disabled={!body.trim()} onClick={addComment}>Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Standard 1 — one delivered/to-do board line: a check/circle toggle, the
 *  text, and a per-line comment thread (type or AI-draft), keyed so the thread
 *  and the done state persist per tester. */
function BoardRow({ itemKey, text, done, onToggle }) {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState('');
  const loadThread = () =>
    api(`/scope/comments/?test_key=${encodeURIComponent(itemKey)}`)
      .then(setThread).catch(() => {});
  useEffect(() => { if (open) loadThread(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const addComment = async () => {
    if (!body.trim()) return;
    await api('/scope/comments/',
              { method: 'POST',
                body: { test_key: itemKey, label: text.slice(0, 200), body: body.trim() } });
    setBody(''); loadThread();
  };
  const bodyBefore = useRef('');
  const draftComment = async () => {
    const d = await api('/scope/comments/assist/',
      { method: 'POST', body: { title: text.slice(0, 120), context: 'scope board item' } });
    return d.text || null;
  };
  return (
    <div className="px-4 py-2.5">
      <div className="flex items-start gap-2">
        <button className={`mt-0.5 text-sm ${done ? 'text-emerald-400' : 'text-slate-600'}`}
                title={done ? 'Done \u2014 click to reopen' : 'To do \u2014 click to mark done'}
                onClick={() => onToggle(itemKey, text, done)}>
          {done ? '\u2713' : '\u25cb'}
        </button>
        <p className="flex-1 text-sm text-slate-200">{text}</p>
        <button className="text-xs text-slate-500 hover:text-vblue shrink-0"
                title="Comment on this line" onClick={() => setOpen(!open)}>✎</button>
      </div>
      {open && (
        <div className="mt-2 ml-6 border-l border-edge pl-3">
          {thread.map((c, k) => (
            <p key={k} className="text-xs text-slate-400 py-0.5">
              <span className="text-slate-300">{c.author}:</span> {c.body}
            </p>
          ))}
          <div className="flex gap-1.5 mt-1">
            <input className="input !py-1 text-xs flex-1" placeholder="Comment…"
                   value={body} onChange={(e) => setBody(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && addComment()} />
            <AIAction size="sm" label="" title="AI drafts a comment"
                      run={() => { bodyBefore.current = body; return draftComment(); }}
                      onResult={setBody}
                      onUndo={() => setBody(bodyBefore.current)}
                      readback="Drafted — edit before you add it." />
            <button className="text-xs border border-edge rounded-lg px-2 text-slate-400"
                    disabled={!body.trim()} onClick={addComment}>Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Scope() {
  const { me } = useAuth();
  const [scope, setScope] = useState(null);
  const [marks, setMarks] = useState({});
  const [error, setError] = useState('');
  const allowed = !!me?.is_superuser;

  useEffect(() => {
    if (!allowed) return;
    api('/scope/site-scope/')
      .then(setScope)
      .catch((e) => setError(e.data?.detail || e.message));
    // §3 complete-marking: pass_if met (my recorded pass) = ✓ done; an open
    // support issue RESURFACES the item (⚠) so regressions can't hide.
    Promise.all([api('/scope/results/').catch(() => []),
                 api('/scope/issues/').catch(() => ({}))])
      .then(([res, iss]) => {
        const m = {};
        for (const r of res) {
          if (r.status === 'pass') m[r.test_key] = 'done';
          else if (r.status === 'pending') m[r.test_key] = 'todo';
        }
        for (const k of Object.keys(iss || {})) m[k] = 'issue';
        setMarks(m);
      });
  }, [allowed]);

  if (!allowed) {
    // Signed out OR signed in without superuser — the same closed door.
    return (
      <div className="min-h-full grid place-items-center px-6">
        <div className="card max-w-sm w-full text-center">
          <img src="/logo.svg" alt="" className="w-10 h-10 mx-auto mb-3" />
          <h1 className="font-bold text-lg">Project scope</h1>
          <p className="text-sm text-slate-400 mt-2">
            This page is restricted. Sign in with an administrator account to
            view the scope.
          </p>
          <Link to="/login" className="btn-primary inline-block mt-4 !px-6">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return <p className="text-red-400 text-sm p-8">{error}</p>;
  }
  if (!scope) {
    return <p className="text-slate-400 text-sm p-8">Loading the scope…</p>;
  }

  const p = scope.project || {};
  const allTests = (scope.groups || []).flatMap((g) => g.tests || []);
  const doneN = allTests.filter((t) => marks[t.key] === 'done').length;
  const issueN = allTests.filter((t) => marks[t.key] === 'issue').length;
  const gradient = scope.design?.tokens?.gradient || [];
  // Tick a delivered/to-do line done or back to to-do; records per tester via
  // the same /scope/save/ the wizard uses (board keys are not hub tests, so the
  // verdict push-back guard skips them).
  const toggleBoard = async (itemKey, text, isDone) => {
    const status = isDone ? 'pending' : 'pass';
    try {
      await api('/scope/save/', { method: 'POST',
        body: { test_key: itemKey, status, label: String(text).slice(0, 200) } });
      setMarks((m) => ({ ...m, [itemKey]: status === 'pass' ? 'done' : 'todo' }));
    } catch { /* best-effort */ }
  };
  return (
    <div className="min-h-full max-w-3xl mx-auto px-6 py-10">
      {/* Vault-branded header: the brain-keyhole, the gradient name, the
          living counts — this page IS the project, dressed as itself. */}
      <div className="card !p-6 mb-6">
        <div className="flex items-center gap-4">
          <img src="/logo.svg" alt="" className="w-12 h-12" />
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-extrabold bg-vault-gradient bg-clip-text
                           text-transparent">
              {p.name || 'Vault'} scope
            </h1>
            <p className="text-sm text-slate-400 truncate">{p.summary}</p>
          </div>
          <span className="text-[10px] uppercase tracking-wide border border-vpurple
                           text-vpurple rounded-full px-2.5 py-1">
            {p.status || 'testing'}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap mt-4">
          {[['✓ done', doneN, 'text-emerald-400'],
            ['○ todo', allTests.length - doneN - issueN, 'text-slate-400'],
            ['⚠ resurfaced', issueN, 'text-amber-400'],
            ['groups', (scope.groups || []).length, 'text-vblue']]
            .map(([label, n, cls]) => (
            <div key={label} className="border border-edge rounded-xl px-3 py-1.5 bg-ink/40">
              <span className={`text-sm font-semibold ${cls}`}>{n}</span>
              <span className="text-[10px] uppercase tracking-wide text-slate-500 ml-1.5">
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Section 1 board: what is DONE and what is still TO DO, each line
          tickable with its own comment thread. */}
      {(scope.delivered || []).length > 0 && (
        <div className="mb-6">
          <h2 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
            Delivered ·{' '}
            {(scope.delivered || []).filter((_, i) =>
              (marks[`delivered-${i}`] ? marks[`delivered-${i}`] === 'done' : true)).length}
            {' '}/ {(scope.delivered || []).length}
          </h2>
          <div className="card !p-0 divide-y divide-edge">
            {(scope.delivered || []).map((text, i) => {
              const k = `delivered-${i}`;
              const done = marks[k] ? marks[k] === 'done' : true;
              return <BoardRow key={k} itemKey={k} text={text} done={done} onToggle={toggleBoard} />;
            })}
          </div>
        </div>
      )}
      {(scope.todo || []).length > 0 && (
        <div className="mb-6">
          <h2 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
            To do ·{' '}
            {(scope.todo || []).filter((_, i) => marks[`todo-${i}`] === 'done').length}
            {' '}/ {(scope.todo || []).length}
          </h2>
          <div className="card !p-0 divide-y divide-edge">
            {(scope.todo || []).map((text, i) => {
              const k = `todo-${i}`;
              const done = marks[k] === 'done';
              return <BoardRow key={k} itemKey={k} text={text} done={done} onToggle={toggleBoard} />;
            })}
          </div>
        </div>
      )}
      {scope.design && (
        <div className="card mb-6">
          <h2 className="font-semibold text-sm mb-2">Design</h2>
          {gradient.length > 0 && (
            <div className="flex items-center gap-1.5 mb-2">
              {gradient.map((c) => (
                <span key={c} className="w-6 h-6 rounded-full border border-edge"
                      style={{ background: c }} title={c} />
              ))}
              <span className="text-[10px] text-slate-500 ml-1">the gradient</span>
              {scope.design.tokens?.currency && (
                <span className="text-[10px] text-slate-500 ml-3">
                  money: {scope.design.tokens.currency}
                </span>
              )}
            </div>
          )}
          <ul className="text-sm text-slate-400 grid gap-1">
            {(scope.design.rules || []).map((r) => <li key={r}>· {r}</li>)}
          </ul>
        </div>
      )}

      {(scope.groups || []).map((g) => (
        <div key={g.group} className="mb-6">
          <h2 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
            {g.group} ·{' '}
            {(g.tests || []).filter((t) => marks[t.key] === 'done').length} done /{' '}
            {g.tests?.length || 0}
          </h2>
          <div className="card !p-0 divide-y divide-edge">
            {(g.tests || []).map((t) => (
              <ScopeItem key={t.key} t={t} mark={marks[t.key]} />
            ))}
          </div>
        </div>
      ))}

      {scope.notes_md && (
        <div className="card">
          <h2 className="font-semibold text-sm mb-2">Notes</h2>
          <pre className="text-xs text-slate-400 whitespace-pre-wrap font-sans">
            {scope.notes_md}
          </pre>
        </div>
      )}
      <NoteBox />
      <p className="text-[10px] text-slate-600 mt-6">
        Served by /api/scope/site-scope/ — superuser only. Full test wizard
        lives at <Link className="underline" to="/testing">/testing</Link>.
      </p>
    </div>
  );
}
